import { Agent, AgentListQuery, OficialStatus, WhatsAppStatus } from '../types/agent';

export interface WhatsAppConfig {
  exigirNome: boolean;
  exigirNomeCompleto: boolean;
  naoAcumularMensagens: boolean;
  shouldBlockAI: boolean;
  numbersToHomologate: string;
}

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

class AgentApiService {
  private getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async getAgentsList(query: AgentListQuery): Promise<Agent[]> {
    const queryParams = new URLSearchParams({
      pageIndex: query.pageIndex.toString(),
      pageSize: query.pageSize.toString(),
      agentName: query.agentName || '',
      agentStatus: query.agentStatus || ''
    });

    const response = await fetch(`${API_BASE_URL}/agent/GetAgentsWithPagination?${queryParams}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch agents');
    }

    return await response.json();
  }

  async getAgent(agentId: string): Promise<Agent> {
    const response = await fetch(`${API_BASE_URL}/agent/Get/${agentId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch agent');
    }

    return await response.json();
  }

  async createAgent(agent: Partial<Agent>): Promise<Agent> {
    const response = await fetch(`${API_BASE_URL}/agent/Create`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(agent),
    });

    if (!response.ok) {
      throw new Error('Failed to create agent');
    }

    return await response.json();
  }

  async updateAgent(agentId: string, agent: Partial<Agent>): Promise<Agent> {
    const response = await fetch(`${API_BASE_URL}/agent/Update/${agentId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(agent),
    });

    if (!response.ok) {
      throw new Error('Failed to update agent');
    }

    return await response.json();
  }

  async deleteAgent(agentId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/agent/Delete/${agentId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to delete agent');
    }
  }

  async duplicateAgent(agentId: string): Promise<Agent> {
    const response = await fetch(`${API_BASE_URL}/agent/DuplicateAgent/${agentId}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to duplicate agent');
    }

    return await response.json();
  }

  async improvePrompt(agentId: string, content: string, model?: string): Promise<string> {
    const response = await fetch(`${API_BASE_URL}/agent/ImprovePrompt/${agentId}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ content, model })
    });

    if (!response.ok) {
      throw new Error('Failed to improve prompt');
    }

    const data = await response.json();
    return data.improvedContent || content;
  }

  async getAgentDetails(agentId: string): Promise<any> {
    // Action-based route: /ChatHistory/AgentUsers?agentId=...
    const q = new URLSearchParams({ agentId });
    const response = await fetch(`${API_BASE_URL}/ChatHistory/AgentUsers?${q.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch agent details');
    }

    return await response.json();
  }

  async clearChatHistory(agentId: string, userId?: string): Promise<void> {
    // Action-based route: /ChatHistory/ClearByUser?agentId=...
    const q = new URLSearchParams({ agentId });
    const response = await fetch(`${API_BASE_URL}/ChatHistory/ClearByUser?${q.toString()}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      throw new Error('Failed to clear chat history');
    }
  }

  async getWhatsAppStatus(agentId: string): Promise<WhatsAppStatus> {
    // Use unified WhatsApp controller used elsewhere in the app
    const response = await fetch(`${API_BASE_URL}/whatsapp/status/${agentId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch WhatsApp status');
    }

    const raw = await response.json();
    const data = raw?.data || raw;
    const jid: string | undefined = raw?.jid;
    const connectionType = raw?.connectionType;
    const whatsappOficialStatus = raw?.status as OficialStatus | undefined;
    
    // Se o connectionType for 'zapi', não mostrar como conectado no WhatsApp nativo
    const isZApiConnection = connectionType === 'zapi';
    const isConnected = isZApiConnection ? false : !!data?.connected;
    const isLoggedIn = isZApiConnection ? false : !!data?.loggedIn;
    const status: WhatsAppStatus['status'] = isConnected && isLoggedIn ? 'connected' : 'disconnected';

    return {
      status,
      isConnected,
      isLoggedIn,
      jid,
      lastCheck: new Date(),
      message: undefined,
      tokenWhatsapp: undefined,
      connectionType,
      whatsappOficialStatus
    };
  }

  async updateWhatsAppConfig(agentId: string, config: WhatsAppConfig): Promise<Agent> {
    const response = await fetch(`${API_BASE_URL}/agent/UpdateWhatsAppConfig/${agentId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(config),
    });

    if (!response.ok) {
      throw new Error('Failed to update WhatsApp configuration');
    }

    return await response.json();
  }


  async updateZApiConfig(agentId: string, config: { clientToken: string; instanceId: string; instanceToken: string }): Promise<Agent> {
    const response = await fetch(`${API_BASE_URL}/agent/UpdateZApiConfig/${agentId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(config),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update Z-API configuration');
    }

    return await response.json();
  }

  async getZApiStatus(agentId: string): Promise<{ connected: boolean; session: boolean; smartphoneConnected: boolean; error?: string; phoneNumber?: string }> {
    const response = await fetch(`${API_BASE_URL}/whatsapp/status/${agentId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to get Z-API status');
    }

    const raw = await response.json();
    const data = raw?.data || raw;
    const jid: string | undefined = raw?.jid;
    const connectionType = raw?.connectionType;
    
    // Se for Z-API, retornar os dados apropriados
    if (connectionType === 'zapi') {
      return {
        connected: data?.connected || false,
        session: data?.loggedIn || false,
        smartphoneConnected: data?.connected || false,
        phoneNumber: jid || undefined,
        error: undefined
      };
    }

    // Fallback para o endpoint original se não for Z-API
    const originalResponse = await fetch(`${API_BASE_URL}/agent/GetZApiStatus/${agentId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!originalResponse.ok) {
      const errorData = await originalResponse.json();
      throw new Error(errorData.message || 'Failed to get Z-API status');
    }

    return await originalResponse.json();
  }

  async disconnectZApi(agentId: string): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/agent/DisconnectZApi/${agentId}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to disconnect Z-API');
    }

    return await response.json();
  }

  async disconnectWhatsApp(agentId: string): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/whatsapp/logout/${agentId}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to disconnect WhatsApp');
    }

    return await response.json();
  }

  async testCRMConfiguration(agentId: string): Promise<{
    message: string;
    success: boolean;
    testData?: {
      leadId: string;
      leadName: string;
      leadPhone: string;
      crmUrl: string;
      method: string;
    };
    crmUrl?: string;
    method?: string;
    error?: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/agent/TestCRMConfiguration/${agentId}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to test CRM configuration');
    }

    return data;
  }

  async previewWhatsAppOficialConnection(data: {
    wabaId: string;
    phoneNumberId: string;
    tokenCode: string;
  }): Promise<{
    wabaId: string;
    phoneNumberId: string;
    phoneNumber: string | null;
    verifiedName: string | null;
    status: string | null;
    codeVerificationStatus: string | null;
    token: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/WhatsAppOficial/PreviewConnection`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || 'Failed to preview WhatsApp Business connection');
    }

    return responseData;
  }

  async connectWhatsAppOficial(agentId: string, data: {
    wabaId: string;
    phoneNumberId: string;
    tokenCode: string;
  }): Promise<{ message: string; success: boolean }> {
    const response = await fetch(`${API_BASE_URL}/WhatsAppOficial/Connect/${agentId}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || 'Failed to connect WhatsApp Oficial');
    }

    return responseData;
  }

  async disconnectWhatsAppOficial(agentId: string): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/WhatsAppOficial/Disconnect/${agentId}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || 'Failed to disconnect WhatsApp Business');
    }

    return responseData;
  }

}

export const agentApi = new AgentApiService();
export default agentApi;